import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type QuestionType = 'checkbox' | 'radio';

interface Option {
  label: string;
  value: string;
}

interface SelectProps {
  /** Sequential number shown before the question */
  index: number;
  /** The question text */
  question: string;
  /** Answer options */
  options: Option[];
  /** 'checkbox' for multi-select, 'radio' for single-select */
  type?: QuestionType;
  /** Controlled value – string[] for checkbox, string for radio */
  value?: string | string[];
  /** Called whenever the selection changes */
  onChange?: (value: string | string[]) => void;
  /** Number of columns for the options grid (default: 2) */
  columns?: 1 | 2 | 3;
  /** Show a divider beneath the question (default: true) */
  showDivider?: boolean;
}

// ─── Shared option-box styles ─────────────────────────────────────────────────

const optionBoxSx = {
  display: 'flex',
  alignItems: 'center',
  border: '1.5px solid',
  borderColor: '#F7F7F9',
  borderRadius: '8px',
  px: 1.5,
  py: 0.75,
  cursor: 'pointer',
  userSelect: 'none',
  background: '#F7F7F9',
  transition: 'border-color 0.15s, background 0.15s',
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

const Select: React.FC<SelectProps> = ({
  index,
  question,
  options,
  type = 'checkbox',
  value,
  onChange,
  columns = 2,
  showDivider = true,
}) => {
  const [internalCheckbox, setInternalCheckbox] = useState<string[]>([]);
  const [internalRadio, setInternalRadio] = useState<string>('');

  // ── Checkbox logic ───────────────────────────────────────────────────────

  const checkboxValues: string[] = value !== undefined ? (value as string[]) : internalCheckbox;

  const handleCheckboxChange = (optionValue: string) => {
    const current = checkboxValues;
    const updated = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue];

    if (value === undefined) setInternalCheckbox(updated);
    onChange?.(updated);
  };

  // ── Radio logic ──────────────────────────────────────────────────────────

  const radioValue: string = value !== undefined ? (value as string) : internalRadio;

  const handleRadioChange = (optionValue: string) => {
    if (value === undefined) setInternalRadio(optionValue);
    onChange?.(optionValue);
  };

  // ── Grid template ────────────────────────────────────────────────────────

  const gridTemplate = `repeat(${columns}, 1fr)`;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Question header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 20,
            flexShrink: 0,
            fontFamily: 'poppins',
            fontSize: '16px',
            lineHeight: 1.5,
            color: '#1C1C1C',
          }}
        >
          {index}.
        </Box>

        <Typography
          fontWeight={400}
          component="div"
          sx={{
            fontFamily: 'poppins',
            fontSize: '16px',
            lineHeight: 1.5,
            color: '#1C1C1C',
            flex: 1,
          }}
        >
          {question}
        </Typography>
      </Box>
      {/* Options */}
      {type === 'checkbox' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: 1.5,
          }}
        >
          {options.map((opt) => {
            const checked = checkboxValues.includes(opt.value);
            return (
              <Box
                key={opt.value}
                onClick={() => handleCheckboxChange(opt.value)}
                sx={{
                  ...optionBoxSx,
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => handleCheckboxChange(opt.value)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{
                    p: 0,
                    mr: 1,
                    color: 'grey.400',
                    background: 'white',
                    '&.Mui-checked': { color: '#1A55F9' },
                  }}
                />
                <Typography
                  fontWeight={400}
                  sx={{
                    fontSize: '16px',
                    fontFamily: 'Poppins',
                    lineHeight: 1.5,
                    color: '#1C1C1C',
                  }}
                >
                  {opt.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <RadioGroup value={radioValue}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              gap: 1.5,
              px: 2,
            }}
          >
            {options.map((opt) => {
              return (
                <Box
                  key={opt.value}
                  onClick={() => handleRadioChange(opt.value)}
                  sx={{
                    ...optionBoxSx,
                  }}
                >
                  <FormControlLabel
                    value={opt.value}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          p: 0,
                          mr: 1,
                          color: 'grey.400',
                          background: 'white',
                          '&.Mui-checked': { color: '#1A55F9' },
                        }}
                      />
                    }
                    label={
                      <Typography
                        fontWeight={400}
                        sx={{
                          fontSize: '16px',
                          fontFamily: 'Poppins',
                          lineHeight: 1.5,
                          color: '#1C1C1C',
                        }}
                      >
                        {opt.label}
                      </Typography>
                    }
                    sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                  />
                </Box>
              );
            })}
          </Box>
        </RadioGroup>
      )}

      {showDivider && <Divider sx={{ mt: 2 }} />}
    </Box>
  );
};

export default Select;
